// Tool-category vocabulary for the v2 RunProgressPanel collapsed rail.
//
// We can't fit one tile per individual tool name in the 60px rail, so
// each `friendlyToolConfig` tool is bucketed into one of a handful of
// visual categories (SQL, Files, Shell, Python, Dashboard, Web, Memory,
// Sub-agents, Tasks, Scripts, Other). The rail then renders one
// category icon per active bucket, with a count badge when multiple
// calls landed in the same bucket.
//
// Keep this in sync with `friendlyToolConfig.ts` — every tool listed
// there should have a category entry; unknown names fall back to
// "other" so a missing entry never blanks the rail.
import React from 'react';
import {
    DataExplorerIcon,
    DashboardIcon,
    BrainIcon,
    ComposerIcon,
    SparkleIcon,
    FileIcon,
    TerminalIcon,
    CodeIcon,
    GlobeIcon,
    UsersIcon,
    ListCheckIcon,
} from './icons';

export type ToolCategoryId =
    | 'sql'
    | 'files'
    | 'shell'
    | 'python'
    | 'dashboard'
    | 'web'
    | 'memory'
    | 'subagent'
    | 'tasks'
    | 'scripts'
    | 'other';

export interface ToolCategoryMeta {
    id: ToolCategoryId;
    label: string;
    Icon: React.FC<React.SVGProps<SVGSVGElement> & { size?: number }>;
}

export const TOOL_CATEGORIES: Record<ToolCategoryId, ToolCategoryMeta> = {
    sql: { id: 'sql', label: 'Banco de dados', Icon: DataExplorerIcon },
    files: { id: 'files', label: 'Arquivos', Icon: FileIcon },
    shell: { id: 'shell', label: 'Terminal', Icon: TerminalIcon },
    python: { id: 'python', label: 'Python', Icon: CodeIcon },
    dashboard: { id: 'dashboard', label: 'Dashboard', Icon: DashboardIcon },
    web: { id: 'web', label: 'Web', Icon: GlobeIcon },
    memory: { id: 'memory', label: 'Memória', Icon: BrainIcon },
    subagent: { id: 'subagent', label: 'Sub-agentes', Icon: UsersIcon },
    tasks: { id: 'tasks', label: 'Tarefas', Icon: ListCheckIcon },
    scripts: { id: 'scripts', label: 'Scripts', Icon: ComposerIcon },
    other: { id: 'other', label: 'Outras', Icon: SparkleIcon },
};

// Ordering used when rendering active categories on the rail — stable
// regardless of the order tools were called, so the rail doesn't jitter
// as new tool types appear mid-turn.
export const CATEGORY_ORDER: ToolCategoryId[] = [
    'sql',
    'files',
    'shell',
    'python',
    'dashboard',
    'web',
    'memory',
    'subagent',
    'tasks',
    'scripts',
    'other',
];

const NAME_TO_CATEGORY: Record<string, ToolCategoryId> = {
    // ── BigQuery / data sources ──
    execute_query: 'sql',
    execute_parallels_queries: 'sql',
    next_page: 'sql',
    list_tables: 'sql',
    get_table_schema: 'sql',
    get_multiple_table_schemas: 'sql',
    remove_data_source: 'sql',
    list_views_and_sources: 'sql',
    create_data_source: 'sql',
    create_or_update_card_view: 'sql',
    create_or_update_python_view: 'sql',
    update_data_source: 'sql',

    // ── Files ──
    create_file: 'files',
    read_file: 'files',
    read_file_tail: 'files',
    patch_file: 'files',
    overwrite_file: 'files',
    list_files: 'files',
    delete_file: 'files',
    file_exists: 'files',

    // ── Shell ──
    command_line: 'shell',

    // ── Python ──
    run_python: 'python',

    // ── Dashboard ──
    get_all_dashboard_dards_data: 'dashboard',
    'imagedescription-analyze_image': 'dashboard',
    'imagedescription-analyze image': 'dashboard',
    analyze_image: 'dashboard',
    patch_card_file: 'dashboard',
    create_update_card: 'dashboard',
    build_dashboard: 'dashboard',
    rebuild_dashboard: 'dashboard',
    list_session_dashboards: 'dashboard',
    save_dashboard: 'dashboard',
    list_dashboard: 'dashboard',

    // ── Web ──
    google_search: 'web',
    curl: 'web',
    markdown_curl: 'web',

    // ── Memory ──
    search_memory_suggestions: 'memory',
    search_memories: 'memory',
    create_memory: 'memory',
    update_memory: 'memory',
    delete_memory: 'memory',
    count_memories: 'memory',

    // ── Sub-agents ──
    start_sub_agent: 'subagent',
    get_sub_agent_status: 'subagent',
    list_sub_agents: 'subagent',
    wait_on_subagent: 'subagent',
    cancel_sub_agent: 'subagent',
    send_message_to_sub_agent: 'subagent',

    // ── Tasks ──
    create_todo_list: 'tasks',
    update_todo_list: 'tasks',

    // ── Scripts ──
    script_create_or_update: 'scripts',
    script_code_execute: 'scripts',
    script_get_metadata: 'scripts',
    script_get: 'scripts',
    script_list: 'scripts',
    script_code_update: 'scripts',
    script_code_commit: 'scripts',
    script_code_rollback: 'scripts',
    script_remove: 'scripts',
};

/**
 * Resolve a tool name to its visual category. Mirrors the skill-prefix
 * stripping that `getFriendlyLabel` performs (e.g.
 * "imagedescription-analyze_image"), so plugin-namespaced tools still
 * land in the right bucket even when only the short form is registered.
 */
export function getToolCategory(toolName: string | undefined): ToolCategoryId {
    if (!toolName) return 'other';
    const direct = NAME_TO_CATEGORY[toolName];
    if (direct) return direct;
    const parts = toolName.split('-');
    if (parts.length > 1) {
        const stripped = parts.slice(1).join('');
        const fromStripped = NAME_TO_CATEGORY[stripped];
        if (fromStripped) return fromStripped;
    }
    return 'other';
}
